import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash } from "react-icons/fa";
import { putRequest } from "../../helpers/api.helper";
import { StepPropsType } from "../../types";
import { useLanguage } from "../../context/LanguageContext";
import ProfileCompletionSuccess from "./ProfileCompletionSuccess";
import VoiceMessageField from "../../components/VoiceMessageField";
import { uploadVoiceMessage } from "../../helpers/voiceMessage.helper";

const MAX_TEXT_BOXES = 10;

const Step10 = ({
  steps,
  profileId,
  currentStep,
  setCurrentStep,
  profileDescriptionEnabled,
  setCompletedSteps,
  onResetFlow,
}: StepPropsType) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [textBoxes, setTextBoxes] = useState([{ id: 1, text: "" }]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [uploadedVoiceUrl, setUploadedVoiceUrl] = useState("");
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);

  useEffect(() => {
    if (!profileDescriptionEnabled) {
      setIsSuccess(true);
    }
  }, [profileDescriptionEnabled]);

  const addTextBox = () => {
    if (textBoxes.length < MAX_TEXT_BOXES) {
      const newId = Math.max(...textBoxes.map((box) => box.id), 0) + 1;
      setTextBoxes([...textBoxes, { id: newId, text: "" }]);
    }
  };

  const removeTextBox = (id: number) => {
    if (textBoxes.length > 1) {
      setTextBoxes(textBoxes.filter((box) => box.id !== id));
    }
  };

  const updateTextBox = (id: number, text: string) => {
    setTextBoxes(
      textBoxes.map((box) => (box.id === id ? { ...box, text } : box)),
    );
  };


  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isVoiceRecording) {
      alert("Please stop voice recording before saving the profile.");
      return;
    }
    setIsLoading(true);
    try {
      const customWords = textBoxes
        .filter((box) => box.text.trim() !== "")
        .map((box) => box.text.trim());

      let voiceMessage = uploadedVoiceUrl || "";
      if (voiceFile) {
        if (!uploadedVoiceUrl) {
          setIsUploadingVoice(true);
          const uploaded = await uploadVoiceMessage(voiceFile, profileId?.userId || "");
          setUploadedVoiceUrl(uploaded.fileUrl);
          voiceMessage = uploaded.fileUrl;
        }
      }

      const response = await putRequest(`/bureau/profile/${profileId?.userId}`, {
        customWords,
        voiceMessage,
        step1: 1,
        step2: 1,
        step3: 1,
        step4: 1,
        step5: 1,
        step6: 1,
        step7: 1,
        step8: 1,
        step9: 1,
        step10: 1,
      });

      if (response.success) {
        setUserData(response.data.user);
        setCompletedSteps?.((prev) =>
          prev.includes(currentStep) ? prev : [...prev, currentStep],
        );
        setIsSuccess(true);
      } else {
        alert("Failed to save custom words.");
      }
    } catch (error: any) {
      console.error("Error saving custom words:", error);
      alert(error?.message || "An error occurred while saving custom words.");
    } finally {
      setIsUploadingVoice(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isSuccess) {
    return (
      <ProfileCompletionSuccess
        userData={userData}
        onGoToDashboard={() => navigate("/admin/dashboard")}
        onClose={() => {
          setIsSuccess(false);
          onResetFlow?.();
        }}
      />
    );
  }

  return (
    <div className="w-full max-w-4xl bg-white">
      <div className="space-y-6 py-4 px-3">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t("Enter Profile description")}
          </h2>
          <p className="text-gray-600">
            Add profile summary text and optionally attach a bureau-recorded
            voice message up to 3 minutes.
          </p>
        </div>

        <div className="space-y-4">
          {textBoxes.map((box, index) => (
            <div key={box.id} className="relative">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-red-500 font-medium">
                  {t("Text Box")} {index + 1}
                </label>
                {textBoxes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTextBox(box.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    title="Remove this text box"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                )}
              </div>
              <textarea
                value={box.text}
                onChange={(e) => updateTextBox(box.id, e.target.value)}
                placeholder="Enter your text here..."
                className="w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                rows={5}
                maxLength={500}
                style={{
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                  minHeight: "120px",
                }}
              />
              <div className="text-xs text-gray-500 mt-1">
                {box.text.length}/500 {t("characters")}
              </div>
            </div>
          ))}
        </div>

        {textBoxes.length < MAX_TEXT_BOXES && (
          <div className="text-center">
            <button
              type="button"
              onClick={addTextBox}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FaPlus className="mr-2" />
              {t("Add Text Box")} ({textBoxes.length}/{MAX_TEXT_BOXES})
            </button>
          </div>
        )}

        <VoiceMessageField
          initialUrl={uploadedVoiceUrl}
          onRecordingChange={setIsVoiceRecording}
          onFileChange={(file) => {
            setVoiceFile(file);
            if (file) {
              setUploadedVoiceUrl("");
            }
          }}
        />

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6">
          <button
            onClick={(event) => {
              event.preventDefault();
              setCurrentStep(Math.max(1, currentStep - 1));
            }}
            className="flex-1 px-6 py-3.5 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isUploadingVoice || isVoiceRecording}
            className="flex-1 px-6 py-3.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg disabled:opacity-70"
          >
            {isLoading || isUploadingVoice
              ? "Saving Profile..."
              : isVoiceRecording
                ? "Stop Recording To Save"
              : currentStep === steps.length
                ? t("Save & Complete Profile")
                : t("Next Step")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step10;
